<template>
	<Transition name="slide-fade">
		<div v-if="isVisible" :class="indicatorClasses" class="global-save-indicator">
			<div class="indicator-icon">
				<component :is="iconComponent" />
			</div>
			<div class="indicator-text">
				<div class="indicator-status">{{ statusText }}</div>
				<div v-if="lastSavedText" class="indicator-time">{{ lastSavedText }}</div>
			</div>
		</div>
	</Transition>
</template>

<script>
import { computed } from 'vue'
import { useSaveIndicator } from '@/composables/useSaveIndicator'

export default {
	name: 'GlobalSaveIndicator',

	setup() {
		const { status, lastSaved, isVisible } = useSaveIndicator()

		const statusText = computed(() => {
			switch (status.value) {
				case 'saved':
					return 'Сохранено'
				case 'saving':
					return 'Сохранение...'
				case 'unsaved':
					return 'Есть несохранённые данные'
				case 'offline':
					return 'Нет соединения (в очереди)'
				case 'error':
					return 'Ошибка сохранения'
				default:
					return ''
			}
		})

		const lastSavedText = computed(() => {
			if (!lastSaved.value) return null

			const now = Date.now()
			const diff = now - lastSaved.value
			const seconds = Math.floor(diff / 1000)
			const minutes = Math.floor(seconds / 60)

			if (seconds < 10) return 'только что'
			if (seconds < 60) return `${seconds} сек назад`
			if (minutes < 60) return `${minutes} мин назад`
			
			const date = new Date(lastSaved.value)
			return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
		})

		const indicatorClasses = computed(() => ({
			'indicator-saved': status.value === 'saved',
			'indicator-saving': status.value === 'saving',
			'indicator-unsaved': status.value === 'unsaved',
			'indicator-offline': status.value === 'offline',
			'indicator-error': status.value === 'error',
		}))

		const iconComponent = computed(() => {
			switch (status.value) {
				case 'saved':
					return 'CheckIcon'
				case 'saving':
					return 'LoadingIcon'
				case 'unsaved':
					return 'AlertIcon'
				case 'offline':
					return 'WifiOffIcon'
				case 'error':
					return 'ErrorIcon'
				default:
					return null
			}
		})

		return {
			status,
			statusText,
			lastSavedText,
			indicatorClasses,
			iconComponent,
			isVisible,
		}
	},

	components: {
		CheckIcon: {
			template: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>'
		},
		LoadingIcon: {
			template: '<svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>'
		},
		AlertIcon: {
			template: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>'
		},
		WifiOffIcon: {
			template: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path><path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path><path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line x1="12" y1="20" x2="12.01" y2="20"></line></svg>'
		},
		ErrorIcon: {
			template: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>'
		}
	}
}
</script>

<style scoped>
.global-save-indicator {
	position: fixed;
	bottom: 24px;
	right: 24px;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: white;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	z-index: 9999;
	min-width: 200px;
	transition: all 0.3s ease;
}

.indicator-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	flex-shrink: 0;
}

.indicator-text {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.indicator-status {
	font-size: 14px;
	font-weight: 600;
	line-height: 1.2;
}

.indicator-time {
	font-size: 11px;
	color: #6b7280;
	line-height: 1.2;
}

/* Статусы */
.indicator-saved .indicator-icon {
	background: #d1fae5;
	color: #059669;
}

.indicator-saving .indicator-icon {
	background: #dbeafe;
	color: #2563eb;
}

.indicator-unsaved .indicator-icon {
	background: #fef3c7;
	color: #d97706;
}

.indicator-offline .indicator-icon {
	background: #fee2e2;
	color: #dc2626;
}

.indicator-error .indicator-icon {
	background: #fee2e2;
	color: #dc2626;
}

.indicator-saved .indicator-status {
	color: #059669;
}

.indicator-saving .indicator-status {
	color: #2563eb;
}

.indicator-unsaved .indicator-status {
	color: #d97706;
}

.indicator-offline .indicator-status {
	color: #dc2626;
}

.indicator-error .indicator-status {
	color: #dc2626;
}

/* Анимации */
.slide-fade-enter-active {
	transition: all 0.3s ease;
}

.slide-fade-leave-active {
	transition: all 0.3s ease;
}

.slide-fade-enter-from {
	transform: translateX(100%);
	opacity: 0;
}

.slide-fade-leave-to {
	transform: translateX(100%);
	opacity: 0;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.animate-spin {
	animation: spin 1s linear infinite;
}
</style>
